import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { requireSession } from '@/lib/ensureSession';
import { useUserStore } from '@/store/userStore';

// ════════════════════════════════════════════════════════════════
// Ежедневная отметка состояния — топливо для связок «эмоции ↔ еда».
// Сон спрашиваем утром (пока он точный), день — вечером с 19:30
// (до окна срывов: по данным 31% последних приёмов приходится на 21:00–23:00,
// поэтому настроение, отмеченное раньше, работает предсказателем).
// Единственная точка, где день закрывается, — вечер: если утро пропущено,
// сон добирается там же. Утро — возможность ответить точнее, а не обязанность.
// ════════════════════════════════════════════════════════════════

export interface DailyCheckin {
  user_id: string;
  date: string;
  sleep: number | null;
  sleep_source: 'morning' | 'evening' | null;
  mood: number | null;
  mood_at: string | null;
  tags: string[];
  note: string | null;
}

// Полным день считается по отметке дня — сон может быть добран вечером.
export const isDayComplete = (c?: DailyCheckin | null) => !!c?.mood;

export const MORNING_UNTIL = 12; // до 12:00 спрашиваем сон
export const EVENING_FROM = 19.5; // с 19:30 спрашиваем день

export function localDate(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Понедельник текущей недели — по нему считаем прогресс к разбору
function weekStart(): string {
  const d = new Date();
  const shift = (d.getDay() + 6) % 7; // пн = 0
  d.setDate(d.getDate() - shift);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function useCheckin() {
  const { profile } = useUserStore();
  const [week, setWeek] = useState<DailyCheckin[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      // Берём с прошлого понедельника — хватает и на прогресс недели, и на «вчера»
      const { data } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', profile.id)
        .gte('date', localDate(-8))
        .order('date', { ascending: false });
      setWeek((data ?? []) as DailyCheckin[]);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const byDate = (d: string) => week.find((c) => c.date === d) ?? null;
  const today = byDate(localDate());
  const yesterday = byDate(localDate(-1));

  const now = new Date();
  const hour = now.getHours() + now.getMinutes() / 60;

  const needsSleep = !today?.sleep;
  const needsDay = !today?.mood;
  // Утром зовём только за сном; вечером — за днём (и заодно добираем сон)
  const askSleep = needsSleep && (hour < MORNING_UNTIL || hour >= EVENING_FROM);
  const askDay = needsDay && hour >= EVENING_FROM;
  // Вчера можно закрыть только до полудня — дальше отметка задним числом уже выдумывается
  const canBackfillYesterday = !yesterday?.mood && hour < MORNING_UNTIL;

  // Отметок за текущую неделю (пн–вс) — прогресс к полному разбору
  const ws = weekStart();
  const filledDays = week.filter((c) => c.date >= ws && !!c.mood).length;

  async function save(date: string, patch: Partial<DailyCheckin>) {
    if (!profile?.id || saving) return;
    if (!(await requireSession())) return;
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('daily_checkins')
        .upsert({ user_id: profile.id, date, ...patch }, { onConflict: 'user_id,date' })
        .select()
        .single();
      if (error) throw error;
      setWeek((prev) => {
        const rest = prev.filter((c) => c.date !== date);
        return [data as DailyCheckin, ...rest].sort((a, b) => (a.date < b.date ? 1 : -1));
      });
    } finally {
      setSaving(false);
    }
  }

  const saveSleep = (v: number, date = localDate()) =>
    save(date, { sleep: v, sleep_source: hour < MORNING_UNTIL ? 'morning' : 'evening' });

  const saveDay = (v: number, extra?: { tags?: string[]; note?: string | null }, date = localDate()) =>
    save(date, { mood: v, mood_at: new Date().toISOString(), ...extra });

  return {
    week,
    today,
    yesterday,
    loading,
    saving,
    askSleep,
    askDay,
    canBackfillYesterday,
    filledDays,
    saveSleep,
    saveDay,
    save,
    reload: load,
  };
}
